import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { LuSquareArrowOutUpRight } from 'react-icons/lu';
import MobileTokenPanel from './MobileTokenPanel';
import TokenIcon from './TokenIcon';
import { useAuth } from '../features/auth';
import { useCheckout } from '../features/checkout';
import { useLocker } from '../features/locker';
import { useToast } from '../features/toast';
import { auth } from '../services/firebase';

const MobileAppHeader = () => {
  const { user, logout, requireLogin, resendVerification } = useAuth();
  const { dismissTokenCheckout, optimisticTokensSpent, tokenSpendEvent, tokenGainEvent } = useCheckout();
  const { lockerGainEvent } = useLocker();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const lockerActive = location.pathname === '/locker';

  const [showTokenPanel, setShowTokenPanel] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [tokenDelta, setTokenDelta] = useState(null);
  const tokenDeltaRef = useRef(null);
  const [lockerFlash, setLockerFlash] = useState(null);
  const previousUserIdRef = useRef(Symbol('init'));
  const [supportOpen, setSupportOpen] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);

  const userTokens = typeof user?.tokens === 'number' ? user.tokens : 0;
  const displayTokens = Math.max(userTokens - optimisticTokensSpent, 0);
  const lockerItems = Array.isArray(user?.locker)
    ? user.locker
    : Array.isArray(user?.history)
    ? user.history
    : [];
  const lockerCount = lockerItems.length;
  const displayName = user?.displayName || user?.username || user?.email || 'PonPon Fan';
  const canOpenLocker = Boolean(user);

  const handleLogout = useCallback(() => {
    if (typeof logout === 'function') {
      logout().catch(() => {});
    }
    setAccountMenuOpen(false);
  }, [logout]);

  const handleLockerClick = useCallback(() => {
    if (!user) {
      requireLogin?.();
      return;
    }
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      showToast({
        message: (
          <>
            Verify your email to unlock all features.&nbsp;
            <button
              type="button"
              onClick={() => {
                try {
                  resendVerification?.();
                } catch (_) {}
              }}
              className="font-semibold underline underline-offset-2 hover:opacity-80"
            >
              Resend.
            </button>
          </>
        ),
        tone: 'info',
      });
      return;
    }
    navigate('/locker');
    setAccountMenuOpen(false);
  }, [user, requireLogin, showToast, resendVerification, navigate]);

  const handleTokenPanelClose = useCallback(() => {
    setShowTokenPanel(false);
    dismissTokenCheckout();
  }, [dismissTokenCheckout]);

  const handleSupportNavigate = useCallback((path) => {
    setAccountMenuOpen(false);
    navigate(path);
  }, [navigate]);

  useEffect(() => {
    const nextUserId = user?.id ?? null;
    if (previousUserIdRef.current === nextUserId) {
      return;
    }
    previousUserIdRef.current = nextUserId;
    setShowTokenPanel(false);
    setAccountMenuOpen(false);
    dismissTokenCheckout();
  }, [user, dismissTokenCheckout]);

  // CHANGED: Close menus when route changes
  useEffect(() => {
    setShowTokenPanel(false);
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!accountMenuOpen) {
      setSupportOpen(false);
      setFollowOpen(false);
    }
  }, [accountMenuOpen]);


  useEffect(() => {
    if (!tokenSpendEvent || !Number.isFinite(Number(tokenSpendEvent.amount))) {
      return;
    }
    const payload = {
      amount: Number(tokenSpendEvent.amount),
      id: tokenSpendEvent.id ?? Date.now(),
      kind: 'spend',
    };
    setTokenDelta(payload);
    tokenDeltaRef.current = payload.id;
    const timeout = setTimeout(() => {
      if (tokenDeltaRef.current === payload.id) {
        setTokenDelta(null);
        tokenDeltaRef.current = null;
      }
    }, 900);
    return () => clearTimeout(timeout);
  }, [tokenSpendEvent]);

  useEffect(() => {
    if (!tokenGainEvent || !Number.isFinite(Number(tokenGainEvent.amount))) {
      return;
    }
    const payload = {
      amount: Number(tokenGainEvent.amount),
      id: tokenGainEvent.id ?? Date.now(),
      kind: 'gain',
    };
    setTokenDelta(payload);
    tokenDeltaRef.current = payload.id;
    const timeout = setTimeout(() => {
      if (tokenDeltaRef.current === payload.id) {
        setTokenDelta(null);
        tokenDeltaRef.current = null;
      }
    }, 900);
    return () => clearTimeout(timeout);
  }, [tokenGainEvent]);

  useEffect(() => {
    if (!lockerGainEvent || !Number.isFinite(Number(lockerGainEvent.amount))) {
      return;
    }
    setLockerFlash({
      amount: Number(lockerGainEvent.amount),
      id: lockerGainEvent.id ?? Date.now(),
    });
    const timeout = setTimeout(() => setLockerFlash(null), 900);
    return () => clearTimeout(timeout);
  }, [lockerGainEvent]);

  return (
    <>
      <header className="mobile-app-header fixed inset-x-0 top-0 z-[60] border-b border-white/40 bg-white/80 pb-2 pt-[calc(env(safe-area-inset-top,0px)+0.4rem)] shadow-sm backdrop-blur-xl">
        <div className="mobile-app-header-bar mx-auto flex w-full max-w-6xl items-center justify-between gap-2 px-4">
          <Link to="/" aria-label="Go to homepage" className="mobile-app-header-logo flex items-center gap-2">
            <img src="/assets/ponpon-logo.svg" alt="PonPon! logo" className="h-9 w-auto" />
          </Link>
          {user ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowTokenPanel(true)}
                className="mobile-header-token-button flex h-9 items-center gap-1 rounded-full border border-white/60 bg-white/90 px-2.5 text-xs font-semibold text-ponpon-navy shadow-sm transition hover:border-ponpon-coral/40 hover:text-ponpon-coral"
              >
                <TokenIcon className="w-4 h-4" alt="Tokens" />
                <span
                  key={tokenDelta?.id || 'token-count-mobile'}
                  className="min-w-[2.1rem] text-center text-sm"
                >
                  {displayTokens}
                </span>
                {tokenDelta ? (
                  <span
                    key={`delta-${tokenDelta.id}`}
                    className={`text-xs font-semibold ${
                      tokenDelta.kind === 'gain' ? 'text-ponpon-teal' : 'text-ponpon-coral'
                    }`}
                    aria-live="polite"
                  >
                    {tokenDelta.kind === 'gain' ? `+${tokenDelta.amount}` : `-${tokenDelta.amount}`}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={canOpenLocker ? handleLockerClick : undefined}
                disabled={!canOpenLocker || lockerActive}
                className={[
                  'mobile-header-locker-button',
                  'flex h-9 items-center gap-1 rounded-full border px-2.5 text-xs font-semibold shadow-sm transition',
                  lockerActive
                    ? 'border-ponpon-coral/60 bg-ponpon-sky/30 text-ponpon-coral'
                    : 'border-white/60 bg-white/90 text-ponpon-navy hover:border-ponpon-coral/40 hover:text-ponpon-coral',
                  (!canOpenLocker || lockerActive) &&
                    'cursor-not-allowed opacity-60 hover:border-white/60 hover:text-ponpon-navy',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <img
                  src="/assets/locker-icon.png"
                  alt="Locker"
                  className="w-4 h-4 object-contain flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="min-w-[1.5rem] text-right text-sm">{lockerCount}</span>
                {lockerFlash ? (
                  <span
                    key={`locker-delta-${lockerFlash.id}`}
                    className="text-xs font-semibold text-ponpon-teal"
                    aria-live="polite"
                  >
                    +{lockerFlash.amount}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => setAccountMenuOpen(true)}
                className="mobile-header-account-button flex h-9 items-center justify-center rounded-full border border-white/60 bg-white/90 px-3 text-sm font-semibold text-ponpon-navy shadow-sm transition hover:border-ponpon-coral/40 hover:text-ponpon-coral"
                aria-label="Account menu"
              >
                <span aria-hidden="true" className="text-base leading-none">👤</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => requireLogin?.()}
              className="mobile-header-login-button rounded-full bg-ponpon-coral px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#E23A32]"
            >
              Join / Sign in
            </button>
          )}
        </div>
      </header>

      {showTokenPanel ? (
        <div
          className="mobile-header-panel-overlay fixed inset-0 z-50 flex items-end bg-ponpon-navy/40 px-4 pb-6 pt-20 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={handleTokenPanelClose}
        >
          <div
            className="mobile-header-panel-card w-full rounded-3xl border border-white/70 bg-white/95 p-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <MobileTokenPanel onClose={handleTokenPanelClose} />
          </div>
        </div>
      ) : null}

      {accountMenuOpen ? (
        <div
          className="mobile-header-account-overlay fixed inset-0 z-50 flex items-end bg-ponpon-navy/40 px-4 pb-6 pt-20 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setAccountMenuOpen(false)}
        >
          <div
            className="mobile-header-account-card w-full rounded-3xl border border-white/70 bg-white/95 p-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setAccountMenuOpen(false)}
              aria-label="Close account menu"
              className="mobile-header-account-close mx-auto mb-3 block h-1.5 w-10 rounded-full bg-ponpon-navy/15 transition hover:bg-ponpon-navy/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ponpon-coral/60"
            />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-ponpon-navy/50">Account</p>
              <p className="text-base font-semibold text-ponpon-navy">{displayName}</p>
            </div>
            <div className="mt-4 space-y-2">
              <div className="mobile-header-howto-card rounded-2xl border border-ponpon-navy/10 bg-white/90 px-4 py-3 text-sm">
                <Link
                  to="/tutorial"
                  onClick={() => setAccountMenuOpen(false)}
                  className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wide text-ponpon-navy/60 transition hover:text-ponpon-coral"
                >
                  <span>How to Play</span>
                  <LuSquareArrowOutUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                </Link>
              </div>
              <div className="mobile-header-support-card rounded-2xl border border-ponpon-navy/10 bg-white/90 px-4 py-3 text-sm">
                <button
                  type="button"
                  onClick={() => setSupportOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wide text-ponpon-navy/60 transition hover:text-ponpon-coral"
                  aria-expanded={supportOpen}
                >
                  <span>Support</span>
                  <span className="text-base leading-none">{supportOpen ? '−' : '+'}</span>
                </button>
                {supportOpen ? (
                  <div className="mt-2 flex flex-col gap-1">
                    <button
                      type="button"
                      className="text-left text-sm font-semibold text-ponpon-navy transition hover:text-ponpon-coral"
                      onClick={() => handleSupportNavigate('/about')}
                    >
                      About
                    </button>
                    <button
                      type="button"
                      className="text-left text-sm font-semibold text-ponpon-navy transition hover:text-ponpon-coral"
                      onClick={() => handleSupportNavigate('/faq')}
                    >
                      FAQ
                    </button>
                    <button
                      type="button"
                      className="text-left text-sm font-semibold text-ponpon-navy transition hover:text-ponpon-coral"
                      onClick={() => handleSupportNavigate('/terms')}
                    >
                      Terms of Service
                    </button>
                    <button
                      type="button"
                      className="text-left text-sm font-semibold text-ponpon-navy transition hover:text-ponpon-coral"
                      onClick={() => handleSupportNavigate('/privacy')}
                    >
                      Privacy Policy
                    </button>
                    <button
                      type="button"
                      className="text-left text-sm font-semibold text-ponpon-navy transition hover:text-ponpon-coral"
                      onClick={() => handleSupportNavigate('/return-refund')}
                    >
                      Return & Refund
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="mobile-header-follow-card rounded-2xl border border-ponpon-navy/10 bg-white/90 px-4 py-3 text-sm">
                <button
                  type="button"
                  onClick={() => setFollowOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wide text-ponpon-navy/60 transition hover:text-ponpon-coral"
                  aria-expanded={followOpen}
                >
                  <span>Follow Us</span>
                  <span className="text-base leading-none">{followOpen ? '−' : '+'}</span>
                </button>
                {followOpen ? (
                  <div className="mt-2 flex flex-col gap-1">
                    <a
                      href="https://www.instagram.com/ponpon.app/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 rounded-xl py-1.5 text-left text-sm font-semibold text-ponpon-navy transition hover:text-ponpon-coral"
                    >
                      <FaInstagram className="h-6 w-6 shrink-0" aria-hidden />
                      <span>Instagram</span>
                    </a>
                    <a
                      href="https://www.tiktok.com/@ponpon.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 rounded-xl py-1.5 text-left text-sm font-semibold text-ponpon-navy transition hover:text-ponpon-coral"
                    >
                      <FaTiktok className="h-6 w-6 shrink-0" aria-hidden />
                      <span>TikTok</span>
                    </a>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full rounded-2xl border border-ponpon-coral/40 bg-ponpon-sky/30 px-4 py-3 text-left text-sm font-semibold text-ponpon-coral transition hover:bg-ponpon-sky/50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default MobileAppHeader;
